import * as Model from '../models';
import * as I18n from '../models/i18n';
import * as Common from './common';
import { hasLocalFileSupport } from '../models/client';
import { generateId } from '../models/util';

import { API_ENDPOINTS, ApiEndpoint, AppConfigService } from './app-config-service';

export type AddProjectRequest = {
  index: number;
  id: string;
};

export type AddOligoRequest = {
  index: number;
  id: string;
  preference?: Model.ProjectPreference;
};

export type ImportProjectTemplateRequest = {
  files: Model.FileItem[];
  langCode?: string;
};

export type ImportProjectTemplateResponse = {
  data: Model.ProjectTemplateData[];
  errors: Model.OligoItemError[];
};

export type ImportProjectRequest = {
  preference: Model.ImportProjectPreference;
  fixErrorsUsingPreference?: boolean;
} & ImportProjectTemplateRequest;

export type ImportProjectResponse = {
  errors: Model.OligoItemError[];
  projects: Model.ProjectItem[];
};

export type BatchOligoParams = { [key: string]: Model.OligoParam };
export type BatchOligoParamDefs = { [key: string]: Model.OligoParamDefs };

type ProjectTemplateResourceData = {
  fivePrimeMod: string;
  oligoName: string;
  purification: string;
  researcherName: string;
  scale: string;
  sequence: string;
  specialHandling: string;
  threePrimeMod: string;
};

type ProjectTemplateResourceDataResponse = {
  error?: Array<{
    code: string;
    message: string;
  }>;
  rawTemplateData: ProjectTemplateResourceData[];
};

type BulkUploadApiPreference = {
  scale: string;
  purification: string;
  formulation: string;
  delivery: string;
};

type BulkUploadApiResponse = {
  error?: Array<{
    code: string;
    message: string;
    lineNumber: number;
  }>;
  oligosParamDefs?: Model.OligoParamDefs[];
};

export interface ProjectService {
  oligoNamesDefinition(parameter: Model.OligoParam): ng.IPromise<Model.OligoParamDefs>;
  oligoParameterDefinition(parameter: Model.OligoParam): ng.IPromise<Model.OligoParamDefs>;
  oligoParameterDefinitionBatch(parameters: BatchOligoParams): ng.IPromise<BatchOligoParamDefs>;
  newOligoItem(request: AddOligoRequest): ng.IPromise<Model.OligoItem>;
  newProjectItem(request: AddProjectRequest): ng.IPromise<Model.ProjectItem>;
  importProject(request: ImportProjectRequest): ng.IPromise<ImportProjectResponse>;
  processProjectImportData(data: BulkUploadApiResponse, preference: Model.ImportProjectPreference): ng.IPromise<ImportProjectResponse>;
  importProjectTemplateData(request: ImportProjectTemplateRequest): ng.IPromise<ImportProjectTemplateResponse>;
  processProjectTemplateData(data: ProjectTemplateResourceDataResponse, langCode: string): ng.IPromise<ImportProjectTemplateResponse>;
}

type RestOligoConfig = {
  oligoConfig: Common.OligRequestParam;
};

type RestOligoConfigBatch = {
  oligoConfigMap: {[key: string]: Common.OligRequestParam};
};

function oligoParamToOligoServiceConfig(parameter: Model.OligoParam): Common.OligRequestParam {
  const { basic, advanced } = parameter;
  return {
    ...basic,
    ...advanced,
  };
}

function paramDefToParam(paramDef: Model.ClientParamDef<string>): Model.Parameter {
  const { id, value } = paramDef;
  return { id, value };
}

function oligoParamDefToOligoParam(paramDef: Model.OligoParamDefs): Model.OligoParam {
  const { basic, advanced } = paramDef;
  const { scale, purification, format, prime3Mod, prime5Mod, delivery, researcherName, oligoName, oligoValue } = basic;
  const { alternate, handling } = advanced;
  return {
    basic: {
      researcherName: paramDefToParam(researcherName),
      oligoName: paramDefToParam(oligoName),
      oligoValue: paramDefToParam(oligoValue),
      scale: paramDefToParam(scale.selected),
      format: paramDefToParam(format.selected),
      purification: paramDefToParam(purification.selected),
      delivery: paramDefToParam(delivery.selected),
      prime3Mod: paramDefToParam(prime3Mod.selected),
      prime5Mod: paramDefToParam(prime5Mod.selected),
    },
    advanced: {
      alternate: alternate.selected.map(paramDefToParam),
      handling: handling.selected.map(paramDefToParam),
    },
  };
}

// the service will validate using the default
// selections specified by the rule, however, since the validation
// is sequence based, sequence errors will get flagged by the rule
// since new oligos don't really have any sequence by default.
// but we don't want to show the users invalid selection when they
// haven't done anything right. so let's make sure that all initial
// selected items are marked as valid then let re-validation kick
// in when they actually do sometime
function ensureSelectedAreValid(definition: Model.OligoParamDefs): Model.OligoParamDefs {
  const { basic } = definition;
  const { scale, purification, format, prime3Mod, prime5Mod } = basic;
  [
    scale.selected,
    purification.selected,
    format.selected,
    prime3Mod.selected,
    prime5Mod.selected,
  ].filter((paramDef: Model.ClientParamDef<string>) => typeof paramDef === 'object')
    .forEach((paramDef: Model.ClientParamDef<string>) => paramDef.invalid = false);
  return definition;
}

function pristineSelection(definition: Model.OligoParamDefs): Model.OligoParamDefs {
  const { basic } = definition;
  const { scale, purification, prime3Mod, prime5Mod, format } = basic;
  [scale, purification, prime3Mod, prime5Mod, format]
    .forEach((paramDef: Model.SelectionClientParamDef) => {
      if (paramDef.selected) {
        paramDef.selected.disabled = true;
        paramDef.selected.invalid = false;
      }
      paramDef.value.forEach((selectionDef: Model.SkuClientParamDef<string>) => {
        selectionDef.disabled = true;
      });
    });
  return definition;
}

type OligoParamDefsByResearcherMap = { [key: string]: Model.OligoParamDefs[] };

function groupByResearcher(definitions: Model.OligoParamDefs[]): OligoParamDefsByResearcherMap {
  const oligoByResearcherMap: OligoParamDefsByResearcherMap = {};
  return definitions.reduce((acc, next: Model.OligoParamDefs) => {
    const { basic } = next;
    const { researcherName } = basic;
    const { value } = researcherName;
    const paramDefs: Model.OligoParamDefs[] = acc[value] || [];
    paramDefs.push(next);
    acc[value] = paramDefs;
    return acc;
  }, oligoByResearcherMap);
}

function asPlainParameter(id: string, value: string): Model.Parameter {
  return {
    value,
    id,
  };
}

export class RestProjectServiceImpl implements ProjectService {

  constructor(protected $q: ng.IQService,
              protected $http: ng.IHttpService,
              protected configService: AppConfigService) {
  }

  public oligoNamesDefinition(parameter: Model.OligoParam): ng.IPromise<Model.OligoParamDefs> {
    const deferred: ng.IDeferred<Model.OligoParamDefs> = this.$q.defer();
    const request: RestOligoConfig = {
      oligoConfig: oligoParamToOligoServiceConfig(parameter),
    };
    const path: string = `${this.configService.pathFor(API_ENDPOINTS.OLIGO_VALIDATE)}?type=name`;
    this.$http.post<Model.OligoParamDefs>(path, request).then((response: ng.IHttpResponse<Model.OligoParamDefs>) => {
      if (response.status === 200) {
        deferred.resolve(response.data);
      } else {
        deferred.reject();
      }
    }, deferred.reject);
    return deferred.promise;
  }

  public oligoParameterDefinition(parameter: Model.OligoParam): ng.IPromise<Model.OligoParamDefs> {
    const deferred: ng.IDeferred<Model.OligoParamDefs> = this.$q.defer();
    const request: RestOligoConfig = {
      oligoConfig: oligoParamToOligoServiceConfig(parameter),
    };
    const path: string = this.configService.pathFor(API_ENDPOINTS.OLIGO_VALIDATE);
    this.$http.post<Model.OligoParamDefs>(path, request).then((response: ng.IHttpResponse<Model.OligoParamDefs>) => {
      if (response.status === 200) {
        deferred.resolve(response.data);
      } else {
        deferred.reject();
      }
    }, deferred.reject);
    return deferred.promise;
  }

  public oligoParameterDefinitionBatch(parameters: BatchOligoParams): ng.IPromise<BatchOligoParamDefs> {
    const deferred: ng.IDeferred<BatchOligoParamDefs> = this.$q.defer();
    const path: string = `${this.configService.pathFor(API_ENDPOINTS.OLIGO_VALIDATE)}?batch=true`;
    const request: RestOligoConfigBatch = Object.keys(parameters).reduce((acc: RestOligoConfigBatch, id: string) => {
      const parameter: Model.OligoParam = parameters[id];
      const { oligoConfigMap } = acc;
      oligoConfigMap[id] = oligoParamToOligoServiceConfig(parameter);
      return acc;
    }, { oligoConfigMap: {} } as RestOligoConfigBatch);
    this.$http.post<BatchOligoParamDefs>(path, request).then((response: ng.IHttpResponse<BatchOligoParamDefs>) => {
      if (response.status === 200) {
        deferred.resolve(response.data);
      } else {
        deferred.reject();
      }
    }, deferred.reject);
    return deferred.promise;
  }

  public newOligoItem(request: AddOligoRequest): ng.IPromise<Model.OligoItem> {
    const { index, preference } = request;
    const path: string = this.configService.pathFor(API_ENDPOINTS.OLIGO_DEFAULT);
    const deferred: ng.IDeferred<Model.OligoItem> = this.$q.defer();
    this.$http.get<Model.OligoParamDefs>(path, { responseType: 'json' })
      .then((response: ng.IHttpResponse<Model.OligoParamDefs>) => {
        if (response.status === 200) {
          const definition: Model.OligoParamDefs = ensureSelectedAreValid(response.data);
          const parameter: Model.OligoParam = oligoParamDefToOligoParam(definition);
          const resolveItem = (targetDefinition: Model.OligoParamDefs): void => {
            const { basic } = targetDefinition;
            const { oligoName, oligoValue, researcherName } = basic;
            [oligoName, oligoValue, researcherName]
              .forEach((textInputParamDef: Model.TextInputClientParamDef) => {
                textInputParamDef.pristine = true;
              });
            const id: string = `${request.id || `${generateId()}_${index}`}`;
            const item: Model.OligoItem = {
              id,
              index,
              parameter,
              definition: pristineSelection(targetDefinition),
              displayType: Model.OligoDisplayTypes.DETAILS,
              configDisplayType: Model.OligoConfigDisplayTypes.BASIC,
            };
            deferred.resolve(item);
          };
          if (preference && !preference.isDefault) {
            const { basic } = parameter;
            basic.scale = { ...preference.scale };
            basic.purification = { ...preference.purification };
            basic.format = { ...preference.format };
            this.oligoParameterDefinition(parameter)
              .then(resolveItem, deferred.reject);
          } else {
            resolveItem(definition);
          }
        } else {
          deferred.reject();
        }
      }, deferred.reject);
    return deferred.promise;
  }

  public newProjectItem(request: AddProjectRequest): ng.IPromise<Model.ProjectItem> {
    const { index } = request;
    const defer: ng.IDeferred<Model.ProjectItem> = this.$q.defer();
    const oligoIdx: number = 0;
    const projectId: string = `${request.id || `${generateId()}_${index}`}`;
    const oligoId: string = `${generateId()}_${oligoIdx}_${projectId}`;
    this.newOligoItem({
      index: oligoIdx,
      id: oligoId,
    }).then((oligo: Model.OligoItem) => {
        const { basic } = oligo.parameter;
        const preference: Model.ProjectPreference = {
          format: { ...basic.format },
          purification: { ...basic.purification },
          scale: { ...basic.scale },
          isDefault: true,
        };
        const item: Model.ProjectItem = {
          index,
          preference,
          id: projectId,
          name: `Project ${index + 1}`,
          oligos: [oligo],
        };
        defer.resolve(item);
      });
    return defer.promise;
  }

  public importProject(request: ImportProjectRequest): ng.IPromise<ImportProjectResponse> {
    const { files, preference, fixErrorsUsingPreference } = request;
    const fileItem: Model.FileItem = files[0];
    const { name: fileName, blob: file } = fileItem;
    const deferred: ng.IDeferred<ImportProjectResponse> = this.$q.defer();
    const targetEndpoint: ApiEndpoint = fixErrorsUsingPreference ? API_ENDPOINTS.BULK_UPLOAD_OVERRIDE : API_ENDPOINTS.BULK_UPLOAD;
    const path: string = `${this.configService.pathFor(targetEndpoint)}`;
    const bulkUploadPreference: BulkUploadApiPreference = {
      scale: preference.scale.id,
      purification: preference.purification.id,
      formulation: preference.format.id,
      delivery: preference.delivery.id,
    };
    const jsonData: string = JSON.stringify(bulkUploadPreference);
    this.$http<BulkUploadApiResponse>({
      method: 'POST',
      url: path,
      headers: {
        'Content-Type': undefined,
      },
      responseType: 'json',
      data: {
        file,
        fileName,
        jsonData,
      },
      transformRequest: (data) => {
        const formData = new FormData();
        Object.keys(data).forEach((key: string) => {
          const value = data[key];
          formData.append(key, value);
        });
        return formData;
      },
    }).then((response: ng.IHttpResponse<BulkUploadApiResponse>) => {
      const { status, data } = response;
      if (status === 200) {
        this.processProjectImportData(data, preference)
          .then(deferred.resolve, deferred.reject);
      } else {
        deferred.reject();
      }
    }, deferred.reject);
    return deferred.promise;
  }

  public processProjectImportData(data: BulkUploadApiResponse, preference: Model.ImportProjectPreference): ng.IPromise<ImportProjectResponse> {
    const { error: uploadErrors, oligosParamDefs: paramDefs } = data;
    const paramDefsByResearcher: OligoParamDefsByResearcherMap = groupByResearcher(paramDefs || []);
    const projects: Model.ProjectItem[] = Object.keys(paramDefsByResearcher).map((key: string, projectIndex: number) => {
      const projectId: string = `${generateId()}_${projectIndex}`;
      const paramDefsForResearcher: Model.OligoParamDefs[] = paramDefsByResearcher[key];
      const oligoItems: Model.OligoItem[] = paramDefsForResearcher.map((paramDef: Model.OligoParamDefs, oligoIndex: number) => {
        const oligoId: string = `${generateId()}_${oligoIndex}_${projectId}`;
        const parameter: Model.OligoParam = oligoParamDefToOligoParam(paramDef);
        const oligoItem: Model.OligoItem = {
          parameter,
          id: oligoId,
          index: oligoIndex,
          definition: paramDef,
          // This is a requirement. The first oligo in a project is expanded to show details by default
          displayType: oligoIndex === 0 ? Model.OligoDisplayTypes.DETAILS : Model.OligoDisplayTypes.SUMMARY,
          configDisplayType: Model.OligoConfigDisplayTypes.BASIC,
        };
        return oligoItem;
      });
      const item: Model.ProjectItem = {
        id: projectId,
        index: projectIndex,
        preference: {
          format: { ...preference.format },
          purification: { ...preference.purification },
          scale: { ...preference.scale },
          isDefault: preference.isDefault,
        },
        name: `Project ${projectIndex + 1}`,
        oligos: oligoItems,
      };
      return item;
    });
    const errors: Model.OligoItemError[] = (uploadErrors || []).map((uploadError) => {
      return {
        id: uploadError.code,
        message: I18n.replacePlaceHolders(uploadError.message, { [I18n.PlaceHolderKeys.OLIGO_INDEX]: `${uploadError.lineNumber}` }),
      };
    });
    return this.$q.resolve<ImportProjectResponse>({ errors, projects });
  }

  public importProjectTemplateData(request: ImportProjectTemplateRequest): ng.IPromise<ImportProjectTemplateResponse> {
    const deferred: ng.IDeferred<ImportProjectTemplateResponse> = this.$q.defer();
    const langCode: string = request.langCode || '';
    const worldPromise: ng.IPromise<Model.WorldData> = this.configService.getAppContent(langCode)
      .catch(() => ({ ...Model.WORLD_INITIAL }));
    worldPromise.then((world: Model.WorldData) => {
      const { vocabulary, templateSupportedExtensions } = world;
      const { files } = request;
      if (files.length > 1) {
        const multipleFilesUnsupportedError: Model.OligoItemError = {
          id: I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_MULTIPLE_FILES_UNSUPPORTED_TEXT,
          message: vocabulary[I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_MULTIPLE_FILES_UNSUPPORTED_TEXT] || 'You may drag in or upload only 1 template file. Please review your data.',
        };
        deferred.reject(multipleFilesUnsupportedError);
        return;
      }
      const fileItem: Model.FileItem = files[0];
      const { name: fileName, blob: file } = fileItem;
      const fileExtension: string = fileName.substring(fileName.lastIndexOf('.'));
      const isUnsupportedFileExtension: boolean = templateSupportedExtensions.filter((extension) => extension === fileExtension).length <= 0;
      if (isUnsupportedFileExtension) {
        let unsupportedFileExtensionMessage: string = vocabulary[I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_TEMPLATE_EXTENSION_UNSUPPORTED_TEXT] ||
          `User file format is not supported. Please review your template file. Accepted file formats: {{${I18n.PlaceHolderKeys.SUPPORTED_TEMPLATE_EXTENSIONS}}}`;
        unsupportedFileExtensionMessage = I18n.replacePlaceHolders(unsupportedFileExtensionMessage, {
          [I18n.PlaceHolderKeys.SUPPORTED_TEMPLATE_EXTENSIONS]: templateSupportedExtensions.join(', ').replace(/,\s([^,]+)$/, ' and $1'),
        });
        const unsupportedFileExtensionError: Model.OligoItemError = {
          id: I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_TEMPLATE_EXTENSION_UNSUPPORTED_TEXT,
          message: unsupportedFileExtensionMessage,
        };
        deferred.reject(unsupportedFileExtensionError);
        return;
      }
      const path: string = `${this.configService.pathFor(API_ENDPOINTS.BULK_TEMPLATE_CONTENT)}`;
      this.$http<ProjectTemplateResourceDataResponse>({
        method: 'POST',
        url: path,
        headers: {
          'Content-Type': undefined,
        },
        responseType: 'json',
        data: {
          file,
          fileName,
        },
        transformRequest: (data) => {
          const formData = new FormData();
          Object.keys(data).forEach((key: string) => {
            const value = data[key];
            formData.append(key, value);
          });
          return formData;
        },
      }).then((response: ng.IHttpResponse<ProjectTemplateResourceDataResponse>) => {
        const { status, data } = response;
        if (status === 200) {
          this.processProjectTemplateData(data, langCode)
            .then(deferred.resolve, deferred.reject);
        } else {
          deferred.reject();
        }
      },  deferred.reject);
    });
    return deferred.promise;
  }

  public processProjectTemplateData(data: ProjectTemplateResourceDataResponse, langCode: string): ng.IPromise<ImportProjectTemplateResponse> {
    const worldPromise: ng.IPromise<Model.WorldData> = this.configService.getAppContent(langCode)
      .catch(() => ({ ...Model.WORLD_INITIAL }));
    return worldPromise.then((world: Model.WorldData) => {
      const { vocabulary } = world;
      const { rawTemplateData, error: uploadErrors } = data;
      const templateData: Model.ProjectTemplateData[] = rawTemplateData.map((result): Model.ProjectTemplateData => {
        const { researcherName, oligoName, sequence, scale, purification, fivePrimeMod, threePrimeMod, specialHandling } = result;
        return {
          researcherName: asPlainParameter(Model.ProjectTemplateColumnDefIds.RESEARCHER_NAME, researcherName),
          oligoName: asPlainParameter(Model.ProjectTemplateColumnDefIds.OLIGO_NAME, oligoName),
          oligoValue: asPlainParameter(Model.ProjectTemplateColumnDefIds.OLIGO_VALUE, sequence),
          scale: asPlainParameter(Model.ProjectTemplateColumnDefIds.SCALE, scale),
          purification: asPlainParameter(Model.ProjectTemplateColumnDefIds.PURIFICATION, purification),
          prime5Mod: asPlainParameter(Model.ProjectTemplateColumnDefIds.PRIME_5_MOD, fivePrimeMod),
          prime3Mod: asPlainParameter(Model.ProjectTemplateColumnDefIds.PRIME_3_MOD, threePrimeMod),
          specialHandling: asPlainParameter(Model.ProjectTemplateColumnDefIds.SPECIAL_HANDLING, specialHandling),
        };
      });
      const errors: Model.OligoItemError[] = (uploadErrors || []).map((uploadError) => {
        return {
          id: uploadError.code,
          message: uploadError.message,
        };
      });
      if (templateData.length <= 0) {
        if (errors.length > 0) {
          throw errors;
        } else {
          const dataEmptyError: Model.OligoItemError = {
            id: I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_TEMPLATE_EMPTY_TEXT,
            message: vocabulary[I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_TEMPLATE_EMPTY_TEXT] || 'Empty template detected. Please review your data.',
          };
          throw dataEmptyError;
        }
      } else {
        return { errors, data: templateData };
      }
    });
  }

}

export class NoFileSupportRestProjectServiceImpl extends RestProjectServiceImpl {

  private importProjectForm: HTMLFormElement;
  private fileNameInput: HTMLInputElement;
  private fileContentInput: HTMLInputElement;
  private jsonDataInput: HTMLInputElement;
  private importDeferred?: ng.IDeferred<ImportProjectResponse>;

  private fetchTemplateDataForm: HTMLFormElement;
  private fetchTemplateDataFileName: HTMLInputElement;
  private fetchTemplateDeferred?: ng.IDeferred<ImportProjectTemplateResponse>;

  private langeCode?: string;

  constructor(protected $q: ng.IQService,
              protected $http: ng.IHttpService,
              protected configService: AppConfigService) {
    super($q, $http, configService);
    const importProjectForm: HTMLFormElement = document.createElement('form');
    importProjectForm.id = 'no-file-support-import-form';
    importProjectForm.method = 'POST';
    importProjectForm.target = 'no-file-support-import-frame';
    importProjectForm.enctype = 'multipart/form-data';
    importProjectForm.style.display = 'none';

    const fileNameInput: HTMLInputElement = document.createElement('input');
    fileNameInput.type = 'hidden';
    fileNameInput.name = 'fileName';
    const fileContentInput: HTMLInputElement = document.createElement('input');
    fileContentInput.type = 'hidden';
    fileContentInput.name = 'fileContent';
    const jsonDataInput: HTMLInputElement = document.createElement('input');
    jsonDataInput.type = 'hidden';
    jsonDataInput.name = 'jsonData';

    const importProjectIframe: HTMLIFrameElement = document.createElement('iframe');
    importProjectIframe.tabIndex = -1;
    importProjectIframe.name = 'no-file-support-import-frame';
    importProjectIframe.style.display = 'none';
    importProjectIframe.addEventListener('load', () => {
      const { importDeferred: deferred } = this;
      if (!deferred) {
        return;
      }
      const responseData: string = (importProjectIframe.contentDocument as Document).body.textContent as string;
      this.processProjectImportData(JSON.parse(responseData), JSON.parse(jsonDataInput.value))
        .then(deferred.resolve, deferred.reject);
    });

    importProjectForm.appendChild(fileNameInput);
    importProjectForm.appendChild(fileContentInput);
    importProjectForm.appendChild(jsonDataInput);
    importProjectForm.appendChild(importProjectIframe);

    document.body.appendChild(importProjectForm);

    this.importProjectForm = importProjectForm;
    this.fileNameInput = fileNameInput;
    this.fileContentInput = fileContentInput;
    this.jsonDataInput = jsonDataInput;

    const fetchTemplateDataForm: HTMLFormElement = document.createElement('form');
    fetchTemplateDataForm.id = 'no-file-support-template-data-form';
    fetchTemplateDataForm.method = 'POST';
    fetchTemplateDataForm.target = 'no-file-support-template-data-frame';
    fetchTemplateDataForm.enctype = 'multipart/form-data';
    fetchTemplateDataForm.style.display = 'none';

    const fetchTemplateDataFileName: HTMLInputElement = document.createElement('input');
    fetchTemplateDataFileName.type = 'hidden';
    fetchTemplateDataFileName.name = 'fileName';

    const fetchTemplateDataFile: HTMLInputElement = document.createElement('input');
    fetchTemplateDataFile.id = 'no-file-support-template-data-file';
    fetchTemplateDataFile.type = 'file';
    fetchTemplateDataFile.name = 'file';

    const fetchTemplateDataFrame: HTMLIFrameElement = document.createElement('iframe');
    fetchTemplateDataFrame.tabIndex = -1;
    fetchTemplateDataFrame.name = 'no-file-support-template-data-frame';
    fetchTemplateDataFrame.style.display = 'none';
    fetchTemplateDataFrame.addEventListener('load', () => {
      const { fetchTemplateDeferred: deferred, langeCode } = this;
      if (!deferred) {
        return;
      }
      const responseData: string = (fetchTemplateDataFrame.contentDocument as Document).body.textContent as string;
      this.processProjectTemplateData(JSON.parse(responseData), langeCode || '')
        .then(deferred.resolve, deferred.reject);
    });

    fetchTemplateDataForm.appendChild(fetchTemplateDataFile);
    fetchTemplateDataForm.appendChild(fetchTemplateDataFileName);
    fetchTemplateDataForm.appendChild(fetchTemplateDataFrame);

    document.body.appendChild(fetchTemplateDataForm);

    this.fetchTemplateDataForm = fetchTemplateDataForm;
    this.fetchTemplateDataFileName = fetchTemplateDataFileName;
  }

  public importProjectTemplateData(request: ImportProjectTemplateRequest): ng.IPromise<ImportProjectTemplateResponse> {
    const deferred: ng.IDeferred<ImportProjectTemplateResponse> = this.$q.defer();
    const langCode: string = request.langCode || '';
    const worldPromise: ng.IPromise<Model.WorldData> = this.configService.getAppContent(langCode)
      .catch(() => ({ ...Model.WORLD_INITIAL }));
    worldPromise.then((world: Model.WorldData) => {
      const { vocabulary, templateSupportedExtensions } = world;
      const { files } = request;
      if (files.length > 1) {
        const multipleFilesUnsupportedError: Model.OligoItemError = {
          id: I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_MULTIPLE_FILES_UNSUPPORTED_TEXT,
          message: vocabulary[I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_MULTIPLE_FILES_UNSUPPORTED_TEXT] || 'You may drag in or upload only 1 template file. Please review your data.',
        };
        deferred.reject(multipleFilesUnsupportedError);
        return;
      }
      const fileItem: Model.FileItem = files[0];
      const { name: fileName } = fileItem;
      const fileExtension: string = fileName.substring(fileName.lastIndexOf('.'));
      const isUnsupportedFileExtension: boolean = templateSupportedExtensions.filter((extension) => extension === fileExtension).length <= 0;
      if (isUnsupportedFileExtension) {
        let unsupportedFileExtensionMessage: string = vocabulary[I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_TEMPLATE_EXTENSION_UNSUPPORTED_TEXT] ||
          `User file format is not supported. Please review your template file. Accepted file formats: {{${I18n.PlaceHolderKeys.SUPPORTED_TEMPLATE_EXTENSIONS}}}`;
        unsupportedFileExtensionMessage = I18n.replacePlaceHolders(unsupportedFileExtensionMessage, {
          [I18n.PlaceHolderKeys.SUPPORTED_TEMPLATE_EXTENSIONS]: templateSupportedExtensions.join(', ').replace(/,\s([^,]+)$/, ' and $1'),
        });
        const unsupportedFileExtensionError: Model.OligoItemError = {
          id: I18n.DictionaryKeys.PROJECT_OLIGO_IMPORT_MODAL_TEMPLATE_EXTENSION_UNSUPPORTED_TEXT,
          message: unsupportedFileExtensionMessage,
        };
        deferred.reject(unsupportedFileExtensionError);
        return;
      }
      const path: string = `${this.configService.pathFor(API_ENDPOINTS.BULK_TEMPLATE_CONTENT)}`.replace('?', '.action?');
      this.fetchTemplateDataFileName.value = fileName;
      this.fetchTemplateDataForm.action = path;
      this.fetchTemplateDataForm.submit();
    });
    this.fetchTemplateDeferred = deferred;
    this.langeCode = langCode;
    return deferred.promise;
  }

  public importProject(request: ImportProjectRequest): ng.IPromise<ImportProjectResponse> {
    const deferred: ng.IDeferred<ImportProjectResponse> = this.$q.defer();
    const { preference, files, fixErrorsUsingPreference } = request;
    const [file] = files;
    const bulkUploadPreference: BulkUploadApiPreference = {
      scale: preference.scale.id,
      purification: preference.purification.id,
      formulation: preference.format.id,
      delivery: preference.delivery.id,
    };
    const jsonData: string = JSON.stringify(bulkUploadPreference);
    const targetEndpoint: ApiEndpoint = fixErrorsUsingPreference ? API_ENDPOINTS.BULK_UPLOAD_OVERRIDE : API_ENDPOINTS.BULK_UPLOAD;
    let path: string = `${this.configService.pathFor(targetEndpoint)}`;
    path = fixErrorsUsingPreference ? path.replace('?', '.action?') : `${path}.action`;
    this.fileNameInput.value = file.name;
    this.fileContentInput.value = file.text as string;
    this.jsonDataInput.value = jsonData;
    this.importProjectForm.action = path;
    this.importProjectForm.submit();

    this.importDeferred = deferred;
    return deferred.promise;
  }
}

export const PROJECT_SERVICE_NAME = 'projectService';
export const ProjectServiceFactory = ($q: ng.IQService, $http: ng.IHttpService, configService: AppConfigService): ProjectService => {
  return hasLocalFileSupport() ? new RestProjectServiceImpl($q, $http, configService) : new NoFileSupportRestProjectServiceImpl($q, $http, configService);
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0-0!./src/main/script/service/project-service.ts